import { AUTOMATIC_SUPPORTED_UNITS, PRODUCT_TYPES } from '@schuettflix/interfaces';
import { LocationConfiguration } from '@/modules/location/domain/LocationConfigurationSchema.ts';
import { isLocationWithWeighingData } from '@/modules/location/domain/isLocationWithWeighingData.ts';
import { LocationType } from '@/modules/location/domain/LocationSchema.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { AutomaticProductsFormAdditionalService } from '../schema/getAdditionalServiceSchema.ts';

export type WeighingLocationRequirementValues = {
    productType: PRODUCT_TYPES;
    isSalesTransportIncluded: boolean;
    isPurchaseTransportIncluded: boolean;
    salesMaterialUnit?: AUTOMATIC_SUPPORTED_UNITS;
    salesTransportUnit?: AUTOMATIC_SUPPORTED_UNITS;
    purchaseMaterialUnit?: AUTOMATIC_SUPPORTED_UNITS;
    purchaseTransportUnit?: AUTOMATIC_SUPPORTED_UNITS;
    additionalServices: Partial<AutomaticProductsFormAdditionalService>[];
    loadingLocation?: LocationConfiguration;
    unloadingLocation?: LocationConfiguration;
};

export function isWeighingLocationRequired(values: WeighingLocationRequirementValues) {
    return (
        hasTonUnit(values) &&
        !!values.loadingLocation &&
        !!values.unloadingLocation &&
        isLocationWithWeighingData(values.loadingLocation) &&
        isLocationWithWeighingData(values.unloadingLocation)
    );
}

export function isMissingLocationWithWeighingData(values: WeighingLocationRequirementValues) {
    return (
        hasTonUnit(values) &&
        !!values.loadingLocation &&
        !!values.unloadingLocation &&
        !isLocationWithWeighingData(values.loadingLocation) &&
        !isLocationWithWeighingData(values.unloadingLocation)
    );
}

export type WeighingLocationStaleValues = {
    weighingLocation?: LocationType | null;
    loadingLocation?: LocationConfiguration | null;
    unloadingLocation?: LocationConfiguration | null;
};

export function isWeighingLocationStale({
    weighingLocation,
    loadingLocation,
    unloadingLocation,
}: WeighingLocationStaleValues) {
    if (!weighingLocation) {
        return false;
    }

    const selectedLocation = weighingLocation === 'LOADING' ? loadingLocation : unloadingLocation;

    if (!selectedLocation) {
        return true;
    }

    return !isLocationWithWeighingData(selectedLocation);
}

function hasTonUnit(values: WeighingLocationRequirementValues) {
    const isTransportOnly = isTransportProduct(values.productType);

    if (!isTransportOnly) {
        if (values.salesMaterialUnit === 'TON' || values.purchaseMaterialUnit === 'TON') {
            return true;
        }
    }

    if (isTransportOnly || !values.isSalesTransportIncluded) {
        if (values.salesTransportUnit === 'TON') {
            return true;
        }
    }

    if (isTransportOnly || !values.isPurchaseTransportIncluded) {
        if (values.purchaseTransportUnit === 'TON') {
            return true;
        }
    }

    return values.additionalServices.some(service => service.salesUnit === 'TON' || service.purchaseUnit === 'TON');
}
